import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";
import { ApiError, decodeApiError, networkError } from "./errors";

/** Everything the app knows about the signed-in session lives in `auth.ts`; the client only needs
 * a way to read the token and to announce a 401. Both are injected to keep this file testable. */
type Hooks = { getToken: () => string | null; onUnauthorized: (path: string) => void };
let hooks: Hooks = { getToken: () => null, onUnauthorized: () => {} };
export function configureClient(next: Hooks) {
  hooks = next;
}

// Same origin in every environment: Vite (dev) and nginx (production) proxy /api to the backend.
// The absolute form is needed because Node-based test runners cannot fetch a relative URL.
export const API_BASE = `${window.location.origin}/api`;

const middleware: Middleware = {
  onRequest({ request }) {
    const token = hooks.getToken();
    if (token) request.headers.set("Authorization", `Bearer ${token}`);
    return request;
  },
  async onResponse({ request, response }) {
    if (response.ok) return response;
    const path = new URL(request.url).pathname.replace(/^\/api/, "");
    // A wrong password on the sign-in form is also a 401, but it is an answer, not an expiry.
    if (response.status === 401 && path !== "/auth/login") hooks.onUnauthorized(path);
    throw await decodeApiError(response);
  },
  // The request never got an answer (offline, proxy down): same shape as every other failure.
  onError({ error }) {
    // openapi-fetch routes errors thrown by onResponse here too; keep those as they are.
    return error instanceof ApiError ? error : networkError(error);
  },
};

/** Training mode answers every request from practice data in the browser. While it is set, `api`
 * sends nothing to the backend except what the override itself passes through. */
type FetchFn = (request: Request) => Promise<Response>;
let override: FetchFn | null = null;
export function setFetchOverride(next: FetchFn | null) {
  override = next;
}

// `fetch` is looked up per call so test doubles installed after import are honoured.
export const api = createClient<paths>({
  baseUrl: API_BASE,
  fetch: (request) => (override ?? globalThis.fetch)(request),
});
api.use(middleware);

/** The signed-in session's header, for a request made outside `api` (a streamed read). */
export function authHeader(): Record<string, string> {
  const token = hooks.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** The cursor the server returned for the next page of a list, if any. */
export function nextCursor(response: Response): string | null {
  return response.headers.get("X-Next-Cursor");
}
